import {UseFormReturn} from 'react-hook-form';

import {getSafely} from 'shared/web';

import {BaseVehicle} from '../../types/BaseVehicle';

export interface FormResetOnTypeChangeActionParams {
  form: UseFormReturn<BaseVehicle>;
  /**
   * Whether the currently selected make exists in the new vehicle type's catalog.
   * - `true`  → make is shared between old and new type → preserve make, check model separately
   * - `false` → make does not exist in new type        → clear make and model
   * - `null`  → lookup unavailable (safe fallback)     → clear make and model
   */
  makeExistsInNewType: boolean | null;
  /**
   * Whether the currently selected model exists in the new vehicle type+make catalog.
   * Only meaningful when makeExistsInNewType === true.
   * - `true`  → model is shared in new type+make catalog → preserve model (and make)
   * - `false` → model does not exist in new type+make    → clear model, preserve make
   * - `null`  → lookup unavailable (safe fallback)       → clear model, preserve make
   */
  modelExistsInNewType: boolean | null;
}

export const MAKE_FIELDS: Array<keyof BaseVehicle> = ['formMake', 'make', 'customMake'];

export const MODEL_FIELDS: Array<keyof BaseVehicle> = [
  'formModelFamily',
  'modelFamily',
  'customModelFamily',
];

/**
 * Conditionally resets make, model, and title fields when vehicle type changes.
 *
 * This action owns the make/model/title reset logic for type changes. bodyStyle
 * is handled by the generic formReset action (always cleared, never shared across types).
 *
 * Decision matrix:
 * | makeExistsInNewType | modelExistsInNewType | Result                          |
 * |---------------------|----------------------|---------------------------------|
 * | true                | true                 | preserve make + model + title   |
 * | true                | false / null         | preserve make, clear model      |
 * | false / null        | (any)                | clear make + model + title      |
 *
 * Title strategy:
 * - When both make and model are preserved: do NOT touch title (TitleField keeps it as-is).
 * - When make is preserved but model is cleared: do NOT touch title.
 *   TitleField's own useEffect will update title from the preserved make and the
 *   now-cleared model (e.g. "Ford Fiesta" → "Ford"). Custom titles remain protected
 *   by TitleField's isFieldDirty guard.
 * - When make is cleared (makeExistsInNewType === false | null): also clear title,
 *   because TitleField's guard does NOT clear a non-empty title when computed title
 *   becomes null — so the machine must clear it explicitly.
 *
 * Invariants:
 * - Make and title fields are only cleared when make does not exist in the new catalog
 * - Model fields are cleared unless both make AND model exist in the new catalog
 */
export const formResetOnTypeChangeAction = (
  _context: unknown,
  params: FormResetOnTypeChangeActionParams
): void => {
  const currentValues = params.form.getValues();

  const preserveMake = params.makeExistsInNewType === true;
  const preserveModel = preserveMake && params.modelExistsInNewType === true;

  const fieldsToReset: Array<keyof BaseVehicle> = [
    ...(preserveMake ? [] : (['title', ...MAKE_FIELDS] as Array<keyof BaseVehicle>)),
    ...(preserveModel ? [] : MODEL_FIELDS),
  ];

  fieldsToReset.forEach((fieldName) => {
    const defaultValue = Array.isArray(getSafely(currentValues, fieldName)) ? [] : null;
    params.form.setValue(fieldName, defaultValue, {
      shouldDirty: false,
      shouldValidate: false,
    });
  });
};
